import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Trash2, Plus, Loader2, GripVertical, Check, Layers, CheckCircle, Edit3, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [orderChanged, setOrderChanged] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
      setLoading(false);
    } catch (err) {
      toast.error("Failed to load categories");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    const addToast = toast.loading("Adding...");
    try {
      const res = await api.post('/categories', { name: newCategory });
      setCategories([...categories, res.data]);
      setNewCategory('');
      toast.success("Added!", { id: addToast });
    } catch (err) {
      toast.error("Error adding category", { id: addToast });
    }
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCategories(items);
    setOrderChanged(true);
  };

  const saveCategoryOrder = async () => {
    const saveToast = toast.loading("Saving sequence...");
    try {
      const updatedOrder = categories.map((cat, index) => ({
        id: cat._id,
        sortOrder: index
      }));
      await api.put('/categories/reorder', { categories: updatedOrder });
      toast.success("Order Saved! 🚀", { id: saveToast });
      setOrderChanged(false);
    } catch (err) {
      toast.error("Failed to save order", { id: saveToast });
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return setEditingId(null);
    try {
      const res = await api.put(`/categories/${id}`, { name: editName });
      setCategories(categories.map(c => c._id === id ? res.data : c));
      setEditingId(null);
      toast.success("Updated!");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Products in this category will be deleted! Continue?")) return;
    try {
      await api.delete(`/categories/${id}`);
      setCategories(categories.filter(c => c._id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error("500 Error: Check Backend Console");
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64">
      <Loader2 className="animate-spin text-green-600" size={40} />
      <p className="mt-4 text-gray-500 font-bold italic">Loading categories...</p>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto space-y-8 pb-20 px-4 md:px-0">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Categories</h2>
          <p className="text-gray-500 text-sm font-medium">Reorder and manage your menu</p>
        </div>
        {orderChanged && (
          <button 
            onClick={saveCategoryOrder}
            className="bg-green-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg shadow-green-100 animate-bounce flex items-center gap-2"
          >
            <Check size={18} /> Save Order
          </button>
        )}
      </div>

      {/* Add Form */}
      <form onSubmit={handleAddCategory} className="relative group">
        <input 
          type="text" 
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="Category Name (e.g. Burgers)"
          className="w-full bg-white border border-gray-100 px-6 py-5 rounded-[2rem] font-bold outline-none focus:ring-4 focus:ring-green-500/10 focus:border-green-500 transition-all shadow-sm"
        />
        <button type="submit" className="absolute right-3 top-3 bg-black text-white p-3 rounded-[1.2rem] hover:bg-gray-800 transition-all active:scale-90">
          <Plus size={24} />
        </button>
      </form>

      {/* List Area */}
      <div className="space-y-3">
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="categories">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {categories.map((cat, index) => (
                  <Draggable key={cat._id} draggableId={cat._id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center justify-between p-4 bg-white rounded-[1.8rem] border transition-all ${
                          snapshot.isDragging ? "shadow-2xl border-green-500 scale-[1.02] z-50" : "border-gray-50 shadow-sm"
                        }`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div {...provided.dragHandleProps} className="text-gray-300 hover:text-gray-500 cursor-grab">
                            <GripVertical size={20} />
                          </div>
                          
                          {editingId === cat._id ? (
                            <div className="flex items-center gap-2 flex-1 animate-in zoom-in-95 duration-200">
                              <input 
                                value={editName} 
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full bg-gray-50 border border-green-200 outline-none px-4 py-2 rounded-xl font-bold text-gray-800"
                                autoFocus
                              />
                              <button onClick={() => handleUpdate(cat._id)} className="p-2 bg-green-600 text-white rounded-xl shadow-md"><CheckCircle size={18}/></button>
                              <button onClick={() => setEditingId(null)} className="p-2 bg-gray-100 text-gray-400 rounded-xl"><X size={18}/></button>
                            </div>
                          ) : (
                            <span className="font-bold text-gray-800 text-lg">{cat.name}</span>
                          )}
                        </div>

                        {!editingId && (
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => { setEditingId(cat._id); setEditName(cat.name); }} 
                              className="p-3 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors"
                            >
                              <Edit3 size={18}/>
                            </button>
                            <button 
                              onClick={() => handleDelete(cat._id)} 
                              className="p-3 text-red-100 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            >
                              <Trash2 size={18}/>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {categories.length === 0 && (
          <div className="text-center py-16 bg-white rounded-[2.5rem] border border-dashed border-gray-200">
            <Layers className="mx-auto text-gray-200 mb-4" size={50} />
            <p className="text-gray-400 font-bold">List is empty. Add your first category!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;